import {
  UpdateTeamType,
  Tournament,
  Pulje,
  TeamType,
  User,
  KNOCK_OUT_TOURNAMENT_SIZE,
  Match,
  STATUS,
  Settings,
  MATCH_TYPE,
  ROLES,
  NATIONALITY,
  Referee,
  SpecielMatchReferee,
  dbMatchEvent,
  EVENT_TYPE,
  TournamentEvent,
} from "../types/types";
import { supabase } from "../supabase";

export type dbTournamentEvent = {
  id: string;
  tournament: string;
  title: string;
  startTime: number;
  endTime: number;
  location: string;
  status: string;
  duration: number;
};

export type dbMatchReferee = {
  id: string;
  matchId: string;
  referee: string;
  tournament: string;
};

type NewTeamType = {
  teamName: string;
  group: string;
  club?: string;
};

export type dbLocationType = {
  id?: string;
  location: string;
  tournament: string;
  delay: number;
  order?: number;
  liveStreamUrl?: string | null;
};

export type dbGoal = {
  id: string;
  match: string;
  team: string;
  tournament: string;
  player?: string | null;
  goalNumber: number;
};

export type dbPlayerType = {
  id: string;
  name: string;
  birthday?: string | null;
  number: string;
  team: string;
  tournament: string;
  user?: string | null;
};

export type dbUserType = {
  email: string | null;
  firstname: string | null;
  id: string;
  lastname: string | null;
  role: ROLES;
  number: string | null;
  team: string | null;
};

export type dbPuljeType = {
  knockoutMatchTime: number;
  groupMatchTime: number;
  id?: string;
  puljeName: string;
  puljeSize: KNOCK_OUT_TOURNAMENT_SIZE;
  tournament?: string;
  puljeType: string;
};

export type dbMatchType = {
  awayTeam?: string | null;
  awayTeamLabel?: string | null;
  awayTeamPoints?: number | null;
  duration?: number;
  endTime?: number | null;
  group?: string | null;
  homeTeam?: string | null;
  homeTeamLabel?: string | null;
  homeTeamPoints?: number | null;
  id?: string;
  knockoutMatchId?: string | null;
  location?: string | null;
  matchType?: MATCH_TYPE;
  pulje?: string | null;
  startTime?: number | null;
  status?: STATUS;
  tournament?: string | null;
};

export const dbCreateEvent = async (
  match: string,
  team: string,
  tournament: string,
  eventNumber: number,
  event: EVENT_TYPE,
  playerId?: string | null
) => {
  const { data, error } = await supabase
    .from("match_events")
    .insert({
      match,
      team,
      tournament,
      eventNumber,
      eventType: event,
    })
    .select("*");
  if (data) {
    return data[0].id;
  } else {
    return error?.message;
  }
};

export const dbCreateTournament = async (tournament: Tournament) => {
  const { data, error } = await supabase
    .from("tournaments")
    .insert(tournament)
    .select("*");

  const { data: data2, error: error2 } = await supabase
    .from("settings")
    .insert({ tournament: data ? data[0].id : "" })
    .select("*");
  if (data) {
    return data[0].id;
  } else {
    return error?.message;
  }
};

export const dbCreateGroupMatch = async (match: dbMatchType) => {
  const { data, error } = await supabase
    .from("matches")
    .insert(match)
    .select("*");
  if (data) {
    return data[0].id;
  } else {
    return error?.message;
  }
};

export const dbCreateTeam = async (team: NewTeamType) => {
  const { data, error } = await supabase.from("teams").insert(team).select("*");
  if (data) {
    return data[0].id;
  } else {
    return error?.message;
  }
};

export const dbCreateGroup = async (group: string, pulje: string) => {
  const { data, error } = await supabase
    .from("groups")
    .insert({ name: group, pulje })
    .select("*");
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke oprette pulje");
  }
};

export const dbCreateLocation = async (location: dbLocationType) => {
  const { data, error } = await supabase
    .from("locations")
    .insert(location)
    .select("*");
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke oprette pulje");
  }
};

export const dbCreatePulje = async (pulje: dbPuljeType) => {
  const { data, error } = await supabase
    .from("puljer")
    .insert(pulje)
    .select("*");
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke oprette pulje");
  }
};

export const dbAssignReferee = async (
  matchId: string,
  refereeId: string,
  tournamentId: string
) => {
  const { data, error } = await supabase
    .from("match_referees")
    .insert({ match: matchId, referee: refereeId, tournament: tournamentId })
    .select();
  if (data) {
    return data[0];
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke tilføje dommer");
  }
};

export const dbSetReferee = async (
  matchRefereeId: string,
  refereeId: string
) => {
  const { data, error } = await supabase
    .from("match_referees")
    .update({ referee: refereeId })
    .eq("id", matchRefereeId)
    .select();
  if (data) {
    return data;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere dommer");
  }
};

export const dbUnassignReferee = async (matchRefereeId: string) => {
  const { data, error } = await supabase
    .from("match_referees")
    .delete()
    .eq("id", matchRefereeId)
    .select();
  if (data) {
    return data;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke slette dommer");
  }
};

export const dbCreateReferee = async (name: string, tournamentId: string) => {
  const { data, error } = await supabase
    .from("referees")
    .insert({ name, tournament: tournamentId })
    .select();
  if (data) {
    return data[0];
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke oprette dommer");
  }
};

export const dbUpdateReferee = async (id: string, name: string) => {
  const { data, error } = await supabase
    .from("referees")
    .update({ name })
    .eq("id", id)
    .select();
  if (data) {
    return data;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere dommer");
  }
};

export const dbDeleteReferee = async (id: string) => {
  const { data, error } = await supabase
    .from("referees")
    .delete()
    .eq("id", id)
    .select();
  if (data) {
    return data;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke slette dommer");
  }
};

export const dbUpdateEvent = async (event: dbMatchEvent) => {
  const { data, error } = await supabase
    .from("match_events")
    .update(event)
    .eq("id", event.id)
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere event");
  }
};

export const dbUpdateTournament = async (tournament: Tournament) => {
  const { data, error } = await supabase
    .from("tournaments")
    .update(tournament)
    .eq("id", tournament.id)
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere turnering");
  }
};

export const dbUpdateSettings = async (settings: Settings) => {
  const { data, error } = await supabase
    .from("settings")
    .update(settings)
    .eq("id", settings.id)
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere turnering");
  }
};

export const dbUpdateUserDetails = async (id: string, user: User) => {
  const { data, error } = await supabase
    .from("users")
    .update(user)
    .eq("id", id)
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke oprette bruger");
  }
};

export const dbUpdatePulje = async (id: string, pulje: dbPuljeType) => {
  const { data, error } = await supabase
    .from("puljer")
    .update(pulje)
    .eq("id", id)
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere pulje");
  }
};

export const dbUpdateTeam = async (teamId: string, team: UpdateTeamType) => {
  const { data, error } = await supabase
    .from("teams")
    .update(team)
    .eq("id", teamId)
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere holdet");
  }
};

export const dbUpdateGroup = async (
  pulje: string,
  groupId: string,
  groupName: string
) => {
  const { data, error } = await supabase
    .from("groups")
    .update({ id: groupId, name: groupName, pulje })
    .eq("id", groupId)
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere pulje");
  }
};

export const dbUpdateLocation = async (location: dbLocationType) => {
  const { data, error } = await supabase
    .from("locations")
    .update(location)
    .eq("id", location.id ?? "")
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere pulje");
  }
};

export const dbUpdateGroupMatch = async (
  matchId: string,
  field: keyof dbMatchType,
  value: any
) => {
  const { data, error } = await supabase
    .from("matches")
    .update({ [field]: value })
    .eq("id", matchId)
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere kamp");
  }
};

export const dbUpdateGroupMatch2 = async (
  matchId: string,
  match: dbMatchType
) => {
  const { data, error } = await supabase
    .from("matches")
    .update(match)
    .eq("id", matchId)
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere kamp");
  }
};

export const dbUpdateGroupMatch3 = async (
  homeTeamId: string,
  awayTeamId: string,
  matchId: string
) => {
  const { data, error } = await supabase
    .from("matches")
    .update({ homeTeam: homeTeamId, awayTeam: awayTeamId })
    .eq("id", matchId)
    .select();
  if (data) {
    return data[0].id;
  } else {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere kamp");
  }
};

export const dbUpdateNextRoundMatch = async (
  nextMatchId: string,
  puljeId: string,
  teamId: string,
  isHomeTeam: boolean
) => {
  const field = isHomeTeam ? "homeTeam" : "awayTeam";
  const { data, error } = await supabase
    .from("matches")
    .update({ [field]: teamId })
    .eq("knockoutMatchId", nextMatchId)
    .eq("pulje", puljeId)
    .select();

  if (error) {
    console.error(error.message);
    throw new Error("Kunne ikke opdatere næste runde kamp");
  }

  return data;
};

export const getAllUsers = async () => {
  let { data, error } = await supabase.from("users").select("*");

  if (data) {
    return data;
  } else {
    console.error(error?.message);
    throw new Error("Kunne ikke hente brugere");
  }
};

export const getAllMatches = async (tournamentId: string) => {
  let { data, error } = await supabase
    .from("matches")
    .select("*")
    .eq("tournament", tournamentId);

  if (data) {
    return data;
  } else {
    console.error(error?.message);
    throw new Error("Kunne ikke hente gruppe kampe");
  }
};

export const getAllPlayers = async (tournamentId: string) => {
  let { data, error } = await supabase
    .from("players")
    .select("*")
    .eq("tournament", tournamentId);

  if (data) {
    return data;
  } else {
    console.error(error?.message);
    throw new Error("Kunne ikke hente spillere");
  }
};

export const getAllPlayersOfTeam = async (teamId: string) => {
  let { data, error } = await supabase
    .from("players")
    .select("*")
    .eq("team", teamId);

  if (data) {
    return data;
  } else {
    console.error(error?.message);
    throw new Error("Kunne ikke hente gruppe kampe");
  }
};

export const getAllEvents = async (tournamentId: string) => {
  let { data, error } = await supabase
    .from("match_events")
    .select("*")
    .eq("tournament", tournamentId);

  if (data) {
    return data;
  } else {
    console.error(error?.message);
    throw new Error("Kunne ikke hente events");
  }
};

export const dbDeleteUser = async (userId: string) => {
  return await supabase.from("users").delete().eq("id", userId);
};

export const dbDeleteEvent = async (eventId: string) => {
  return await supabase.from("match_events").delete().eq("id", eventId);
};

export const dbDeleteLocation = async (locationId: string) => {
  const { error } = await supabase
    .from("locations")
    .delete()
    .eq("id", locationId);

  if (error?.message) {
    console.error(error.message);
    throw new Error("Kunne ikke slette banen");
  }
};

export const dbDeleteGroup = async (groupId: string) => {
  const { error } = await supabase.from("groups").delete().eq("id", groupId);

  if (error?.message) {
    console.error(error.message);
    throw new Error("Kunne ikke slette gruppe");
  }
};

export const dbDeleteTeam = async (teamId: string) => {
  const { error } = await supabase.from("teams").delete().eq("id", teamId);

  if (error?.message) {
    console.error(error.message);
    throw new Error("Kunne ikke slette holdet");
  }
};

export const dbDeleteGroupMatch = async (matchId: string) => {
  const { error } = await supabase.from("matches").delete().eq("id", matchId);
};

export const dbDeleteAllMatches = async (tournamentId: string) => {
  const { error } = await supabase
    .from("matches")
    .delete()
    .eq("tournament", tournamentId);
};

export const dbCreateTournamentEvent = async (
  title: string,
  duration: number,
  locationId: string,
  tournamentId: string,
  startTime: number,
  endTime: number,
  status: STATUS
) => {
  const { data, error } = await supabase
    .from("tournament_events")
    .insert({
      title,
      startTime,
      endTime,
      duration,
      location: locationId,
      tournament: tournamentId,
      status,
    })
    .select();

  if (data) {
    return data[0].id;
  } else {
    console.error(error?.message);
    throw new Error("Kunne ikke oprette turneringsbegivenhed");
  }
};

export const dbUploadTournamentEvents = async (events: dbTournamentEvent[]) => {
  const { data, error } = await supabase
    .from("tournament_events")
    .upsert(events)
    .select();
  if (error?.message) {
    console.error(error.message);
    throw new Error("Kunne ikke upload tournament events");
  }
};

export const dbDeleteTournamentEvent = async (eventId: string) => {
  const { error } = await supabase
    .from("tournament_events")
    .delete()
    .eq("id", eventId);
};

export const dbDeletePulje = async (puljeId: string) => {
  const { error } = await supabase.from("puljer").delete().eq("id", puljeId);

  if (error?.message) {
    console.error(error.message);
    throw new Error("Kunne ikke slette pulje");
  }
};

export const dbUploadGroupMatches = async (matches: dbMatchType[]) => {
  const { data, error } = await supabase
    .from("matches")
    .upsert(matches)
    .select();
  if (error?.message) {
    console.error(error.message);
    throw new Error("Kunne ikke upload kampe");
  }
};

export const fetchUserDetails = async (uid: string) => {
  const { data, error } = await supabase
    .from("users")
    .select("*")
    .eq("id", uid);

  if (data) {
    return data[0];
  } else {
    console.error(error);
  }
};

export const sendResetPasswordLink = async (email: string) => {
  try {
    const response = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=${process.env.REACT_APP_API_KEY}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, requestType: "PASSWORD_RESET" }),
      }
    );
    return response.json();
  } catch (error) {
    return error;
  }
};

export const getTournaments = async () => {
  const promise = await supabase.from("tournaments").select("*");

  const { data, error } = promise;

  if (data) {
    return data;
  } else {
    console.error(error);
  }
  return data;
};

export const getTeamById = async (id: string) => {
  const { data, error } = await supabase
    .from("teams")
    .select("*, club(*)")
    .eq("id", id);

  if (data) {
    return data[0];
  } else {
    throw new Error(error.message);
  }
};

export const getPlayers = async (teamId: string) => {
  const { data, error } = await supabase
    .from("players")
    .select("*")
    .eq("team", teamId);

  if (data) {
    return data as dbPlayerType[];
  } else {
    console.error(error);
  }
};

export const getSettings = async (settingsId: string) => {
  const { data, error } = await supabase
    .from("settings")
    .select("*")
    .eq("id", settingsId);

  if (data) {
    return data[0];
  } else {
    console.error(error);
  }
};

export const getActiveTournament = async () => {
  const { data, error } = await supabase
    .from("tournaments")
    .select("*")
    .eq("isActive", true);

  if (data) {
    return data[0];
  } else {
    console.error(error);
  }
};

export const getTournamentData = async (tournamentId: string) => {
  const response = await Promise.all([
    supabase.from("locations").select("*").eq("tournament", tournamentId),
    supabase.from("puljer").select("*").eq("tournament", tournamentId),
    supabase.from("tournaments").select("*").eq("id", tournamentId),
    supabase.from("clubs").select("*").eq("nationality", NATIONALITY.DENMARK),
    supabase.from("settings").select("*").eq("tournament", tournamentId),
    supabase.from("referees").select("*").eq("tournament", tournamentId),
    supabase
      .from("match_referees")
      .select("*, referee: referees!inner(*)")
      .eq("tournament", tournamentId),
    supabase
      .from("tournament_events")
      .select("*, location: locations(*)")
      .eq("tournament", tournamentId),
  ]);

  const tournament = (response[2].data ?? [])[0];
  const tournamentEvents =
    (response[7].data as TournamentEvent[])?.map((event) => ({
      ...event,
      tournament,
    })) ?? [];
  const matchReferees =
    (response[6].data as SpecielMatchReferee[])?.map((ref) => ({
      ...ref,
      tournament,
    })) ?? [];
  const referees =
    (response[5].data as Referee[])?.map((ref) => ({ ...ref, tournament })) ??
    [];
  const settings = (response[4].data as Settings[])[0];
  const clubs = response[3].data ?? [];
  const locations =
    response[0].data?.map((loc) => ({ ...loc, tournament })) ?? [];
  const puljer =
    (response[1].data as Pulje[])?.map((pulje) => ({ ...pulje, tournament })) ??
    [];
  const puljeIds = puljer?.map((obj) => obj.id);

  const response2 = await supabase
    .from("groups")
    .select("*")
    .in("pulje", [...puljeIds]);

  const groups =
    response2.data?.map((group) => {
      return {
        ...group,
        pulje: puljer?.find((pulje) => pulje.id === group.pulje) as Pulje,
      };
    }) ?? [];

  const groupIds = groups?.map((group) => group.id);

  const response3 = await supabase
    .from("teams")
    .select("*")
    .in("group", [...groupIds]);

  const teams =
    response3.data?.map((team) => {
      return {
        ...team,
        club: clubs?.find((club) => club.id === team.club),
        group: groups?.find((group) => group.id === team.group),
      } as TeamType;
    }) ?? [];

  return {
    puljer,
    locations,
    groups,
    teams,
    clubs,
    tournament,
    settings,
    referees,
    matchReferees,
    tournamentEvents,
  };
};

export const uploadFile = async (filename: string, file: any) => {
  const { data, error } = await supabase.storage
    .from("club-logos")
    .upload(filename, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (data) {
    return `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${data.fullPath}`;
  } else {
    console.error(error);
  }
};

export const fetchEarlierMatches = async (
  homeTeamClubId: string,
  awayTeamClubId: string,
  tournamentId: string,
  puljeName: string
) => {
  const { data, error } = await supabase
    .from("matches")
    .select(
      `*, homeTeam (*, club: clubs!inner(*)), awayTeam (*, club: clubs!inner(*)), tournament: tournaments!inner(*), pulje: puljer!inner(*)`
    )
    .in("homeTeam.club.id", [homeTeamClubId, awayTeamClubId])
    .in("awayTeam.club.id", [homeTeamClubId, awayTeamClubId])
    .not("homeTeam", "is", null)
    .not("awayTeam", "is", null)
    .neq("tournament.id", tournamentId)
    .eq("status", STATUS.FINISHED)
    .eq("pulje.puljeName", puljeName);

  const matches = data?.map((match) => match.id) as string[];

  const matchEvents = await getMatchEvents(matches);

  const result = data?.map((match) => {
    return {
      ...match,
      homeTeamEvents:
        matchEvents?.filter(
          (event) =>
            match.id === event.match && event.team === match.homeTeam.id
        ) ?? [],
      awayTeamEvents:
        matchEvents?.filter(
          (event) =>
            match.id === event.match && event.team === match.awayTeam.id
        ) ?? [],
    };
  });

  if (result) {
    return (result as unknown as Match[])
      .sort((a, b) => (b.startTime ?? 0) - (a.startTime ?? 0))
      .filter((match) => match.homeTeam?.club?.id !== match.awayTeam?.club?.id);
  } else {
    console.error(error);
  }
};

export const getMatchEvents = async (matches: string[]) => {
  let { data, error } = await supabase
    .from("match_events")
    .select("*")
    .in("match", matches);

  if (data) {
    return data;
  } else {
    console.error(error?.message);
    throw new Error("Kunne ikke hente events");
  }
};

export const dbClearMatchEvents = async (matchId: string) => {
  const { error } = await supabase
    .from("match_events")
    .delete()
    .eq("match", matchId);

  if (error?.message) {
    console.error(error.message);
    throw new Error("Kunne ikke slette målene");
  }
};

export async function createUser(
  email: string,
  password: string,
  role: string
) {
  // Get the current session
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    throw new Error("User not logged in or session expired");
  }

  const accessToken = sessionData.session.access_token;

  // Call the Edge Function
  const res = await fetch(
    "https://eibupqmiogyhyjfgahnz.functions.supabase.co/create-user",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ email, password, role }),
    }
  );

  // Handle non-200 responses
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Request failed with status ${res.status}`
    );
  }

  // Return data if everything went fine
  const data = await res.json();
  return data;
}

export async function deleteUser(userId: string) {
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    throw new Error("User not logged in or session expired");
  }

  const accessToken = sessionData.session.access_token;

  const res = await fetch(
    "https://eibupqmiogyhyjfgahnz.functions.supabase.co/delete-user",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ userId }),
    }
  );

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Request failed with status ${res.status}`
    );
  }

  const data = await res.json();
  return data;
}
